import { useState, useEffect } from "react";
import { axiosInstance, API } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShoppingBag, Plus, Search, Printer, XCircle, DollarSign, Package, Phone, User, CheckCircle, Clock, Ban } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { printHtmlInWindow } from "../utils/printHelper";

export default function Layaways({ user }) {
  const { selectedBox } = useBox();
  const [layaways, setLayaways] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  // Create layaway
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cart, setCart] = useState([]);
  const [initialPayment, setInitialPayment] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [notes, setNotes] = useState("");

  // Payment dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedLayaway, setSelectedLayaway] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethodAbono, setPaymentMethodAbono] = useState("efectivo");
  const [paymentNotes, setPaymentNotes] = useState("");

  // Detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailLayaway, setDetailLayaway] = useState(null);

  const isMaster = user?.role === "master";

  const fetchLayaways = async () => {
    if (!selectedBox) return;
    setLoading(true);
    try {
      const url = filterStatus === "all" 
        ? `/layaways?box_id=${selectedBox.id}` 
        : `/layaways?box_id=${selectedBox.id}&status=${filterStatus}`;
      const response = await axiosInstance.get(url);
      setLayaways(response.data.layaways || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar apartados"));
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/inventory/products?box_id=${selectedBox.id}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    if (selectedBox) {
      fetchLayaways();
      fetchProducts();
    }
  }, [selectedBox, filterStatus]);

  const getAvailableStock = (product) => {
    return (product.quantity || 0) - (product.reserved_quantity || 0);
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    const product = products.find(p =>
      p.code.toLowerCase() === barcodeInput.trim().toLowerCase()
    );
    if (product) {
      const available = getAvailableStock(product);
      const inCart = cart.find(c => c.product_id === product.id);
      const cartQty = inCart ? inCart.quantity : 0;
      if (cartQty >= available) {
        toast.error(`Stock insuficiente. Disponible: ${available}`);
      } else {
        addToCart(product);
      }
    } else {
      toast.error("Producto no encontrado");
    }
    setBarcodeInput("");
  };

  const addToCart = (product) => {
    const existing = cart.find(c => c.product_id === product.id);
    if (existing) {
      setCart(cart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1, subtotal: (c.quantity + 1) * c.price } : c));
    } else {
      setCart([...cart, { product_id: product.id, code: product.code, name: product.name, quantity: 1, price: product.price, subtotal: product.price }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(c => c.product_id !== productId));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.subtotal, 0);

  const handleCreateLayaway = async () => {
    if (!customerName.trim()) { toast.error("Nombre del cliente es requerido"); return; }
    if (cart.length === 0) { toast.error("Agregue al menos un producto"); return; }
    if (!initialPayment || parseFloat(initialPayment) <= 0) { toast.error("El abono inicial debe ser mayor a 0"); return; }
    if (parseFloat(initialPayment) > cartTotal) { toast.error("El abono no puede ser mayor al total"); return; }

    try {
      const response = await axiosInstance.post("/layaways", {
        box_id: selectedBox.id,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || null,
        products: cart,
        total: cartTotal,
        initial_payment: parseFloat(initialPayment),
        payment_method: paymentMethod,
        notes: notes.trim() || null
      });
      toast.success(response.data.message);

      // Print receipt
      if (response.data.layaway?.id) {
        handlePrintLayaway(response.data.layaway.id);
      }

      setShowCreateDialog(false);
      resetCreateForm();
      fetchLayaways();
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al crear apartado"));
    }
  };

  const resetCreateForm = () => {
    setCustomerName("");
    setCustomerPhone("");
    setCart([]);
    setInitialPayment("");
    setPaymentMethod("efectivo");
    setBarcodeInput("");
    setNotes("");
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) { toast.error("Ingrese un monto válido"); return; }
    try {
      const response = await axiosInstance.post(`/layaways/${selectedLayaway.id}/payment`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethodAbono,
        notes: paymentNotes.trim() || null
      });
      toast.success(response.data.message);

      // Print updated receipt
      handlePrintLayaway(selectedLayaway.id);

      setShowPaymentDialog(false);
      setSelectedLayaway(null);
      setPaymentAmount("");
      setPaymentNotes("");
      fetchLayaways();
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar abono"));
    }
  };

  const handleCancelLayaway = async (layaway) => {
    if (!window.confirm(`¿Cancelar apartado ${layaway.number}? Se devuelven ₡${layaway.total_paid?.toLocaleString()} al cliente.`)) return;
    try {
      const response = await axiosInstance.post(`/layaways/${layaway.id}/cancel`);
      toast.success(response.data.message);
      fetchLayaways();
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cancelar apartado"));
    }
  };

  const handlePrintLayaway = async (layawayId) => {
    try {
      const response = await axiosInstance.get(`/layaways/${layawayId}/print`);
      if (response.data.html) {
        const win = printHtmlInWindow(response.data.html, { width: 400, height: 600 });
        if (!win) toast.error("El navegador bloqueó la ventana. Permite ventanas emergentes.");
      }
    } catch (error) {
      toast.error("Error al imprimir comprobante");
    }
  };

  const openDetail = (layaway) => {
    setDetailLayaway(layaway);
    setShowDetailDialog(true);
  };

  const openPayment = (layaway) => {
    setSelectedLayaway(layaway);
    setPaymentAmount("");
    setPaymentMethodAbono("efectivo");
    setPaymentNotes("");
    setShowPaymentDialog(true);
  };

  const filteredLayaways = layaways.filter(l =>
    !searchTerm || l.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || l.number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusBadge = (status) => {
    const styles = {
      active: "bg-amber-100 text-amber-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700"
    };
    const labels = { active: "Activo", completed: "Completado", cancelled: "Cancelado" };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${styles[status] || "bg-gray-100"}`}>{labels[status] || status}</span>;
  };

  const statusIcon = (status) => {
    if (status === "active") return <Clock className="w-5 h-5 text-amber-500" />;
    if (status === "completed") return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <Ban className="w-5 h-5 text-red-500" />;
  };

  if (!selectedBox) return <div className="p-8 text-center text-slate-500">Seleccione una caja</div>;

  const summary = {
    active: layaways.filter(l => l.status === "active").length,
    totalPending: layaways.filter(l => l.status === "active").reduce((s, l) => s + (l.remaining || 0), 0),
    completed: layaways.filter(l => l.status === "completed").length,
  };

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="layaways-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Apartados</h1>
          <p className="text-slate-500">{selectedBox.name}</p>
        </div>
        <Button onClick={() => { resetCreateForm(); setShowCreateDialog(true); }} data-testid="new-layaway-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Apartado
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{summary.active}</p>
            <p className="text-xs text-amber-700 font-medium">Apartados Activos</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-red-600">₡{summary.totalPending.toLocaleString()}</p>
            <p className="text-xs text-red-700 font-medium">Saldo Pendiente</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-green-600">{summary.completed}</p>
            <p className="text-xs text-green-700 font-medium">Completados</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-1">
          {[{v:"active",l:"Activos"},{v:"completed",l:"Completados"},{v:"cancelled",l:"Cancelados"},{v:"all",l:"Todos"}].map(f => (
            <Button key={f.v} size="sm" variant={filterStatus === f.v ? "default" : "outline"} onClick={() => setFilterStatus(f.v)}>{f.l}</Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input placeholder="Buscar por cliente o número..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
        </div>
      </div>

      {/* Layaways List */}
      {loading ? (
        <p className="text-center py-8 text-slate-500">Cargando...</p>
      ) : filteredLayaways.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay apartados {filterStatus !== "all" ? `con estado "${filterStatus}"` : ""}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredLayaways.map(layaway => (
            <Card key={layaway.id} className={`cursor-pointer hover:shadow-md transition-shadow ${layaway.status === "cancelled" ? "opacity-60" : ""}`}>
              <CardContent className="py-3">
                <div className="flex items-center gap-4">
                  {statusIcon(layaway.status)}
                  <div className="flex-1 min-w-0" onClick={() => openDetail(layaway)}>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-800">{layaway.customer_name}</p>
                      {statusBadge(layaway.status)}
                    </div>
                    <p className="text-xs text-slate-500 font-mono">{layaway.number}</p>
                    <p className="text-xs text-slate-400 truncate">{layaway.products?.map(p => `${p.name} (${p.quantity})`).join(", ")}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-slate-800">₡{layaway.total?.toLocaleString()}</p>
                    {layaway.status === "active" && (
                      <p className="text-sm text-red-600 font-semibold">Saldo: ₡{layaway.remaining?.toLocaleString()}</p>
                    )}
                    {layaway.status === "active" && (
                      <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${Math.min((layaway.total_paid / layaway.total) * 100, 100)}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {layaway.status === "active" && (
                      <Button size="sm" onClick={() => openPayment(layaway)} data-testid="add-payment-btn">
                        <DollarSign className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handlePrintLayaway(layaway.id)} data-testid="print-layaway-btn">
                      <Printer className="w-4 h-4" />
                    </Button>
                    {layaway.status === "active" && isMaster && (
                      <Button size="sm" variant="destructive" onClick={() => handleCancelLayaway(layaway)} data-testid="cancel-layaway-btn">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Layaway Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo Apartado</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cliente *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Nombre del cliente" value={customerName} onChange={e => setCustomerName(e.target.value)} className="pl-9" data-testid="layaway-customer-name" />
                </div>
              </div>
              <div>
                <Label>Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Teléfono" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>

            <div>
              <Label>Agregar Producto (código)</Label>
              <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
                <Input placeholder="Escanear código de barras..." value={barcodeInput} onChange={e => setBarcodeInput(e.target.value)} className="font-mono" data-testid="layaway-barcode" />
                <Button type="submit">Agregar</Button>
              </form>
            </div>

            {/* Product selector */}
            <div className="max-h-32 overflow-y-auto border rounded p-2">
              <p className="text-xs text-slate-500 mb-1">O seleccionar producto:</p>
              <div className="grid grid-cols-2 gap-1">
                {products.filter(p => getAvailableStock(p) > 0).map(p => (
                  <button key={p.id} onClick={() => addToCart(p)} className="text-left text-xs p-1.5 rounded hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-slate-400 ml-1">₡{p.price?.toLocaleString()} ({getAvailableStock(p)} disp.)</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Cart */}
            {cart.length > 0 && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-sm font-semibold mb-2">Productos apartados:</p>
                {cart.map(item => (
                  <div key={item.product_id} className="flex justify-between items-center py-1 border-b border-slate-200 last:border-0">
                    <div>
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-xs text-slate-500 ml-2">x{item.quantity} @ ₡{item.price?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">₡{item.subtotal?.toLocaleString()}</span>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeFromCart(item.product_id)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between mt-2 pt-2 border-t font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-blue-600">₡{cartTotal.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Abono Inicial *</Label>
                <Input type="number" placeholder="₡0" value={initialPayment} onChange={e => setInitialPayment(e.target.value)} data-testid="layaway-initial-payment" />
              </div>
              <div>
                <Label>Método de Pago</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="sinpe">SINPE</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas</Label>
                <Input placeholder="Observaciones..." value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateLayaway} disabled={cart.length === 0 || !customerName.trim()} data-testid="create-layaway-submit">
              <ShoppingBag className="w-4 h-4 mr-2" /> Crear Apartado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar Abono</DialogTitle></DialogHeader>
          {selectedLayaway && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-bold">{selectedLayaway.customer_name}</p>
                <p className="text-xs text-slate-500">{selectedLayaway.number}</p>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div className="text-center">
                    <p className="text-lg font-bold">₡{selectedLayaway.total?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">₡{selectedLayaway.total_paid?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Pagado</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">₡{selectedLayaway.remaining?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Saldo</p>
                  </div>
                </div>
              </div>
              <div>
                <Label>Monto del Abono *</Label>
                <Input type="number" placeholder="₡0" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} data-testid="payment-amount" />
                <Button size="sm" variant="link" className="text-xs p-0 mt-1" onClick={() => setPaymentAmount(selectedLayaway.remaining?.toString())}>
                  Pagar saldo completo (₡{selectedLayaway.remaining?.toLocaleString()})
                </Button>
              </div>
              <div>
                <Label>Método de Pago</Label>
                <Select value={paymentMethodAbono} onValueChange={setPaymentMethodAbono}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="sinpe">SINPE</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notas</Label>
                <Input placeholder="Observaciones..." value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddPayment} data-testid="submit-payment-btn">
              <DollarSign className="w-4 h-4 mr-2" /> Registrar Abono
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detalle de Apartado</DialogTitle></DialogHeader>
          {detailLayaway && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xl font-bold">{detailLayaway.customer_name}</p>
                  <p className="text-sm text-slate-500 font-mono">{detailLayaway.number}</p>
                  {detailLayaway.customer_phone && <p className="text-sm text-slate-500"><Phone className="w-3 h-3 inline mr-1" />{detailLayaway.customer_phone}</p>}
                </div>
                {statusBadge(detailLayaway.status)}
              </div>

              <div className="border rounded-lg p-3">
                <p className="text-sm font-semibold mb-2">Productos:</p>
                {detailLayaway.products?.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                    <span>{p.name} x{p.quantity}</span>
                    <span className="font-medium">₡{p.subtotal?.toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                  <span>Total:</span><span>₡{detailLayaway.total?.toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-blue-50 rounded">
                  <p className="text-xl font-bold">₡{detailLayaway.total?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <p className="text-xl font-bold text-green-600">₡{detailLayaway.total_paid?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Pagado</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <p className="text-xl font-bold text-red-600">₡{detailLayaway.remaining?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Saldo</p>
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Progreso</span>
                  <span>{Math.round((detailLayaway.total_paid / detailLayaway.total) * 100)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2.5">
                  <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: `${Math.min((detailLayaway.total_paid / detailLayaway.total) * 100, 100)}%` }} />
                </div>
              </div>

              <div className="border rounded-lg p-3">
                <p className="text-sm font-semibold mb-2">Historial de Abonos:</p>
                {detailLayaway.payments?.map((pay, i) => (
                  <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b last:border-0">
                    <div>
                      <span className="font-medium">₡{pay.amount?.toLocaleString()}</span>
                      <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${pay.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' : pay.payment_method === 'sinpe' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {pay.payment_method?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{new Date(pay.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-slate-400">{pay.notes}</p>
                    </div>
                  </div>
                ))}
              </div>

              {detailLayaway.notes && (
                <p className="text-sm text-slate-500 italic">Notas: {detailLayaway.notes}</p>
              )}
              <p className="text-xs text-slate-400">Creado: {new Date(detailLayaway.created_at).toLocaleString()} por {detailLayaway.created_by}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handlePrintLayaway(detailLayaway?.id)}>
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            {detailLayaway?.status === "active" && (
              <Button onClick={() => { setShowDetailDialog(false); openPayment(detailLayaway); }}>
                <DollarSign className="w-4 h-4 mr-2" /> Abonar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
